/**
 * Protected Route Component
 * Redirects unauthenticated users to login page
 * Shows 404 for authenticated users without required roles
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuth } from '../../context/AuthContext';
import NotFound from '../../Pages/NotFound';

/**
 * Wrapper for routes that require authentication and/or specific roles
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @param {string} [props.redirectTo='/login'] - Path to redirect if not authenticated
 * @param {string[]} [props.allowedRoles] - Roles that can access this route (any match grants access)
 */
const ProtectedRoute = ({ children, redirectTo = '/login', allowedRoles }) => {
  const { isAuthenticated, loading, hasAnyRole } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth status
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '200px' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the attempted location for redirecting after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check role-based access if allowedRoles are specified
  if (allowedRoles && allowedRoles.length > 0) {
    if (!hasAnyRole(allowedRoles)) {
      // User is authenticated but doesn't have required role - show 404
      return <NotFound />;
    }
  }

  return children;
};

export default ProtectedRoute;
